import React, { useState } from 'react';
import styled from 'styled-components';
import {
  AnalyticsSummaryCards,
  UserAnalyticsTable,
  DeviceUsagePieChart,
  UserEngagementTimelineChart,
  QuestionPopularityChart,
  EngagementQualityRadarChart,
  ContentQualityWaterfallChart,
  QuestionInteractionHeatmap,
  UserResponseTimeGroupedBarChart,
  SentimentAnalysisTrigger,
  SentimentOverviewCard,
  SentimentByUserChart,
  QuestionSentimentChart
} from 'components';

function SessionAnalyticsDashboard({ data, onRefresh }) {
  const [refreshKey, setRefreshKey] = useState(0);

  if (!data) {
    return <div>No analytics data available</div>;
  }

  const handleAnalysisComplete = () => {
    // Trigger refresh of analytics data
    if (onRefresh) {
      onRefresh();
    }
    setRefreshKey((prev) => prev + 1);
  };

  const hasSentimentData = Boolean(
    data.sentimentData && data.sentimentData.overview
  );

  return (
    <Container>
      <SessionHeader>
        <SessionTitle>Session Title: {data.sessionName}</SessionTitle>
        <SessionId>Session ID: {data.sessionId}</SessionId>
      </SessionHeader>

      <AnalyticsSummaryCards totals={data.totals} />

      <TableSection>
        <TableTitle>User Breakdown</TableTitle>
        <UserAnalyticsTable
          users={[...data.users].sort(
            (a, b) => (b.engagementScore || 0) - (a.engagementScore || 0)
          )}
        />
      </TableSection>

      <ChartsSection>
        {data.engagementTimeline && data.engagementTimeline.length > 0 && (
          <ChartContainer>
            <ChartTitle>User Engagement Scores Over Time</ChartTitle>
            <UserEngagementTimelineChart
              data={data.engagementTimeline}
              users={data.users}
            />
          </ChartContainer>
        )}

        <ChartContainer>
          <ChartTitle>Device Usage Distribution</ChartTitle>
          <DeviceUsagePieChart data={data.deviceUsage} />
        </ChartContainer>
      </ChartsSection>

      {data.users && data.users.length > 0 && (
        <WaterfallSection>
          <WaterfallTitle>Engagement Score Breakdown</WaterfallTitle>
          <ContentQualityWaterfallChart users={data.users} />
        </WaterfallSection>
      )}

      {data.users && data.users.length > 0 && (
        <RadarSection>
          <RadarTitle>Engagement Radar</RadarTitle>
          <EngagementQualityRadarChart users={data.users} />
        </RadarSection>
      )}

      <SentimentSection>
        <SectionTitle>Sentiment Analysis</SectionTitle>
        <SentimentAnalysisTrigger
          sessionId={data.sessionId}
          hasSentimentData={hasSentimentData}
          onAnalysisComplete={handleAnalysisComplete}
        />
        {hasSentimentData && (
          <>
            <SentimentOverviewCard sentimentData={data.sentimentData} />
            <SentimentByUserChart sentimentData={data.sentimentData} />
            <QuestionSentimentChart sentimentData={data.sentimentData} />
          </>
        )}
      </SentimentSection>

      {data.questionPopularity && data.questionPopularity.length > 0 && (
        <QuestionSection>
          <QuestionTitle>Question Popularity</QuestionTitle>
          <QuestionPopularityChart data={data.questionPopularity} />
        </QuestionSection>
      )}

      {data.questionInteractionMatrix &&
        data.questionInteractionMatrix.length > 0 && (
          <HeatmapSection>
            <HeatmapTitle>Question Interaction Heatmap</HeatmapTitle>
            <QuestionInteractionHeatmap data={data.questionInteractionMatrix} />
          </HeatmapSection>
        )}

      {data.userResponseTimes && data.userResponseTimes.length > 0 && (
        <ParallelChartSection>
          <ParallelChartTitle>
            User Response Times by Question
          </ParallelChartTitle>
          <UserResponseTimeGroupedBarChart data={data.userResponseTimes} />
        </ParallelChartSection>
      )}
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  margin: 0 auto;
  max-width: 1340px;
`;

const SessionHeader = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  padding: 1.5rem;
`;

const SentimentSection = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  padding: 1.5rem;
`;

const SectionTitle = styled.h3`
  color: var(--brown);
  font-family: 'Poppins', sans-serif;
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0 0 1.5rem 0;
`;

const Message = styled.div`
  background: #f9fafb;
  border: 1px dashed #d1d5db;
  border-radius: 6px;
  color: var(--light-brown);
  font-family: 'Inter', sans-serif;
  font-size: 0.875rem;
  line-height: 1.6;
  margin-bottom: 1rem;
  margin-top: 1rem;
  padding: 1.5rem;
  text-align: left;
`;

const SessionTitle = styled.h2`
  color: var(--brown);
  font-family: 'Poppins', sans-serif;
  font-size: 1.3rem;
  margin: 0 0 0.5rem 0;
`;

const SessionId = styled.p`
  color: var(--light-brown);
  font-family: 'Inter', sans-serif;
  font-size: 0.9rem;
  margin: 0;
`;

const ChartsSection = styled.div`
  display: grid;
  gap: 2rem;
  grid-template-columns: 1fr;

  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const QuestionSection = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const HeatmapSection = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const HeatmapTitle = styled.h3`
  color: var(--brown);
  font-family: 'Poppins', sans-serif;
  font-size: 1.1rem;
  margin: 0;
  padding: 1.5rem 1.5rem 1rem 1.5rem;
`;

const ResponseTimeSection = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const ResponseTimeTitle = styled.h3`
  color: var(--brown);
  font-family: 'Poppins', sans-serif;
  font-size: 1.1rem;
  margin: 0;
  padding: 1.5rem 1.5rem 1rem 1.5rem;
`;

const ParallelChartSection = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const ParallelChartTitle = styled.h3`
  color: var(--brown);
  font-family: 'Poppins', sans-serif;
  font-size: 1.1rem;
  margin: 0;
  padding: 1.5rem 1.5rem 1rem 1.5rem;
`;

const QuestionTitle = styled.h3`
  color: var(--brown);
  font-family: 'Poppins', sans-serif;
  font-size: 1.1rem;
  margin: 0;
  padding: 1.5rem 1.5rem 1rem 1.5rem;
`;

const RadarSection = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const WaterfallSection = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const WaterfallTitle = styled.h3`
  color: var(--brown);
  font-family: 'Poppins', sans-serif;
  font-size: 1.1rem;
  margin: 0;
  padding: 1.5rem 1.5rem 1rem 1.5rem;
`;

const RadarTitle = styled.h3`
  color: var(--brown);
  font-family: 'Poppins', sans-serif;
  font-size: 1.1rem;
  margin: 0;
  padding: 1.5rem 1.5rem 1rem 1.5rem;
`;

const ChartContainer = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  padding: 1.5rem;
`;

const ChartTitle = styled.h3`
  color: var(--brown);
  font-family: 'Poppins', sans-serif;
  font-size: 1.1rem;
  margin: 0 0 1rem 0;
`;

const TableSection = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const TableTitle = styled.h3`
  color: var(--brown);
  font-family: 'Poppins', sans-serif;
  font-size: 1.1rem;
  margin: 0;
  padding: 1.5rem 1.5rem 1rem 1.5rem;
`;

export default SessionAnalyticsDashboard;
